import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'
import { DataSchemaItemFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import BaseNumberField from 'modules/core/components/NumberField'
import i18n from 'modules/core/lib/i18n'

import { validatePayrollField } from 'modules/employees/components/blocks/PayrollEmployee/lib/validationUtils'

interface Props {
  dataSchemaItem: DataSchemaItemFragment
  field: Field
  onValidationError?: (fieldName: string, error: string | undefined) => void
  readonly?: boolean
}

const VALIDATED_FIELDS = [
  'company_number_of_health_insurer',
  'annual_tax_allowance',
  'requested_annual_allowance',
  'health_insurance_id',
  'monthly_tax_allowance',
] as const

export const NumberField = observer(
  ({ dataSchemaItem, field, onValidationError, readonly = false }: Props) => {
    const value = field.value ? Number(field.value) : null
    const [error, setError] = React.useState<string | undefined>(undefined)

    const validateField = React.useCallback(
      (fieldValue: string) => {
        const validationResult = validatePayrollField(dataSchemaItem.fieldName, fieldValue)

        if (!validationResult.isValid) {
          setError(validationResult.error)
          onValidationError?.(dataSchemaItem.fieldName, validationResult.error)
        } else {
          setError(undefined)
          onValidationError?.(dataSchemaItem.fieldName, undefined)
        }
      },
      [dataSchemaItem.fieldName, onValidationError]
    )

    const isValidatedField = (fieldName: string): boolean => {
      return VALIDATED_FIELDS.some((validField) => validField === fieldName)
    }

    const handleChange = React.useCallback(
      (newValue: number | null) => {
        const stringValue = newValue === null ? '' : String(newValue)
        field.set(stringValue)

        if (isValidatedField(dataSchemaItem.fieldName)) {
          validateField(stringValue)
        }
      },
      [field, dataSchemaItem.fieldName, validateField]
    )

    const secondaryLabel = () => {
      switch (dataSchemaItem.fieldName) {
        case 'company_number_of_health_insurer':
          return (
            <InformationTooltip
              information={i18n.t(
                'employee_form.payroll_data.company_number_of_health_insurer_tooltip'
              )}
              icon="question"
            />
          )
        case 'health_insurance_id':
          return (
            <InformationTooltip
              information={i18n.t('employee_form.payroll_data.health_insurance_id_tooltip')}
              icon="question"
            />
          )
        default:
          return null
      }
    }

    return (
      <Box>
        <BaseNumberField
          label={dataSchemaItem.label}
          mandatory={dataSchemaItem.required}
          placeholder={i18n.t('employee_form.payroll_data.enter_value')}
          step={1}
          value={value}
          onChange={handleChange}
          textAlign="left"
          error={error}
          secondaryLabel={secondaryLabel()}
          readonly={readonly}
        />
      </Box>
    )
  }
)
